import { BackSide, Box3, BufferAttribute, BufferGeometry, Clock, Color, DecrementWrapStencilOp, DoubleSide, DynamicDrawUsage, FrontSide, IncrementWrapStencilOp, InterleavedBufferAttribute, Line3, LineBasicMaterial, LineSegments, Matrix4, Mesh, MeshBasicMaterial, NotEqualStencilFunc, Plane, PlaneGeometry, Scene, ShaderMaterial, Vector3, Vector4, ZeroStencilOp } from "three";
import { MeshBVH } from "three-mesh-bvh";
import { GeometryWorkerService } from "../../../services/geometry/geometry-worker.service";

interface ManagedObject {
    originalMesh: Mesh;
    colliderBvh?: MeshBVH;
    colliderMesh?: Mesh;
}

export class Clipper {
    public planeMeshX: Mesh;
    public planeMeshY: Mesh;
    public planeMeshZ: Mesh;
    public outlineLinesX: LineSegments;
    public outlineLinesY: LineSegments;
    public outlineLinesZ: LineSegments;

    public initialClip = false;


    public generateEdges = true;

    private originalMesh?: Mesh;
    private managedObjects: ManagedObject[] = [];

    private planeMaterial: MeshBasicMaterial;

    constructor(public scene: Scene, public clippingPlanes: Plane[]) {
        this.planeMaterial = new MeshBasicMaterial({
            color: '#A4C7E1',
            side: DoubleSide,
            stencilWrite: true,
            stencilFunc: NotEqualStencilFunc,
            stencilFail: ZeroStencilOp,
            stencilZFail: ZeroStencilOp,
            stencilZPass: ZeroStencilOp
        });
        this.planeMeshX = new Mesh(new PlaneGeometry(), this.planeMaterial);
        this.planeMeshY = new Mesh(new PlaneGeometry(), this.planeMaterial);
        this.planeMeshZ = new Mesh(new PlaneGeometry(), this.planeMaterial);

        this.planeMeshX.name = 'PlaneMeshX';
        this.planeMeshY.name = 'PlaneMeshY';
        this.planeMeshZ.name = 'PlaneMeshZ';

        const setupPlaneMesh = (planeMesh: Mesh, renderOrder: number) => {
            planeMesh.renderOrder = renderOrder;
            this.scene.add(planeMesh);
        };

        setupPlaneMesh(this.planeMeshX, 1);
        setupPlaneMesh(this.planeMeshY, 1);
        setupPlaneMesh(this.planeMeshZ, 1);

        // Outline lines are added to scene here so they're available
        // regardless of whether setObject or addObject is used
        const lineGeometryX = new BufferGeometry();
        const linePosAttrX = new BufferAttribute(new Float32Array(300000), 3, false);
        linePosAttrX.setUsage(DynamicDrawUsage);
        lineGeometryX.setAttribute('position', linePosAttrX);
        this.outlineLinesX = new LineSegments(lineGeometryX, new LineBasicMaterial({ color: 0x000000, clippingPlanes: [this.clippingPlanes[1], this.clippingPlanes[2]] }));
        this.outlineLinesX.frustumCulled = false;

        const lineGeometryY = new BufferGeometry();
        const linePosAttrY = new BufferAttribute(new Float32Array(300000), 3, false);
        linePosAttrY.setUsage(DynamicDrawUsage);
        lineGeometryY.setAttribute('position', linePosAttrY);
        this.outlineLinesY = new LineSegments(lineGeometryY, new LineBasicMaterial({ color: 0x000000, clippingPlanes: [this.clippingPlanes[0], this.clippingPlanes[2]] }));
        this.outlineLinesY.frustumCulled = false;

        const lineGeometryZ = new BufferGeometry();
        const linePosAttrZ = new BufferAttribute(new Float32Array(300000), 3, false);
        linePosAttrZ.setUsage(DynamicDrawUsage);
        lineGeometryZ.setAttribute('position', linePosAttrZ);
        this.outlineLinesZ = new LineSegments(lineGeometryZ, new LineBasicMaterial({ color: 0x000000, clippingPlanes: [this.clippingPlanes[0], this.clippingPlanes[1]] }));
        this.outlineLinesZ.frustumCulled = false;

        this.scene.add(this.outlineLinesX, this.outlineLinesY, this.outlineLinesZ);
    }

    setObject(originalMesh: Mesh): void {
        this.originalMesh = originalMesh;

        this.planeMaterial.color.copy((originalMesh.material as MeshBasicMaterial).color);

        const mergedGeometry = originalMesh.geometry;
        originalMesh.quaternion.identity();

        const materialMap = new Map();
        const frontSideModel = originalMesh;
        frontSideModel.updateMatrixWorld(true);
        frontSideModel.traverse((c) => {
            if (c instanceof Mesh) {
                if (materialMap.has(c.material)) {
                    c.material = materialMap.get(c.material);
                    return;
                }

                const material = c.material.clone();
                material.side = FrontSide;
                material.stencilWrite = true;
                material.stencilFail = IncrementWrapStencilOp;
                material.stencilZFail = IncrementWrapStencilOp;
                material.stencilZPass = IncrementWrapStencilOp;
                material.clippingPlanes = this.clippingPlanes;
                material.transparent = false;

                materialMap.set(c.material, material);
                c.material = material;
            }
        });

        materialMap.clear();

        const backSideModel = frontSideModel.clone();
        backSideModel.name += '_BackSideModel';
        backSideModel.traverse((c) => {
            if (c instanceof Mesh) {
                if (materialMap.has(c.material)) {
                    c.material = materialMap.get(c.material);
                    return;
                }

                const material = c.material.clone();
                material.colorWrite = false;
                material.depthWrite = false;
                material.side = BackSide;
                material.stencilWrite = true;
                material.stencilFail = DecrementWrapStencilOp;
                material.stencilZFail = DecrementWrapStencilOp;
                material.stencilZPass = DecrementWrapStencilOp;
                material.clippingPlanes = this.clippingPlanes;
                material.transparent = false;

                materialMap.set(c.material, material);
                c.material = material;
            }
        });

        const colliderBvh = new MeshBVH(mergedGeometry, { maxLeafSize: 3 });
        mergedGeometry.boundsTree = colliderBvh;

        const colliderMesh = new Mesh(mergedGeometry, new MeshBasicMaterial({ depthWrite: false }));
        colliderMesh.name = originalMesh.name + '_Collider';
        colliderMesh.position.copy(originalMesh.position);
        colliderMesh.rotation.copy(originalMesh.rotation);
        colliderMesh.scale.copy(originalMesh.scale);
        colliderMesh.visible = false;

        frontSideModel.visible = true;
        backSideModel.visible = true;

        this.managedObjects.push({ originalMesh, colliderBvh, colliderMesh });

        (originalMesh.parent as Scene).add(backSideModel, colliderMesh);
        originalMesh.parent?.updateMatrixWorld(true);
    }

    addObject(mesh: Mesh, includeStencilCap = true): void {
        const geometry = mesh.geometry;

        if (includeStencilCap) {
            const frontStencilMat = new MeshBasicMaterial({
                colorWrite: false,
                depthWrite: false,
                side: FrontSide,
                stencilWrite: true,
                stencilFail: IncrementWrapStencilOp,
                stencilZFail: IncrementWrapStencilOp,
                stencilZPass: IncrementWrapStencilOp,
                clippingPlanes: this.clippingPlanes
            });
            const frontStencil = new Mesh(geometry, frontStencilMat);
            frontStencil.name = mesh.name + '_StencilFront';
            frontStencil.position.copy(mesh.position);
            frontStencil.rotation.copy(mesh.rotation);
            frontStencil.scale.copy(mesh.scale);

            const backStencilMat = new MeshBasicMaterial({
                colorWrite: false,
                depthWrite: false,
                side: BackSide,
                stencilWrite: true,
                stencilFail: DecrementWrapStencilOp,
                stencilZFail: DecrementWrapStencilOp,
                stencilZPass: DecrementWrapStencilOp,
                clippingPlanes: this.clippingPlanes
            });
            const backStencil = new Mesh(geometry, backStencilMat);
            backStencil.name = mesh.name + '_StencilBack';
            backStencil.position.copy(mesh.position);
            backStencil.rotation.copy(mesh.rotation);
            backStencil.scale.copy(mesh.scale);

            mesh.parent?.add(frontStencil, backStencil);
        }

        const colliderBvh = new MeshBVH(geometry, { maxLeafSize: 3 });

        const colliderMesh = new Mesh(geometry, new MeshBasicMaterial({ depthWrite: false }));
        colliderMesh.name = mesh.name + '_Collider';
        colliderMesh.position.copy(mesh.position);
        colliderMesh.rotation.copy(mesh.rotation);
        colliderMesh.scale.copy(mesh.scale);
        colliderMesh.visible = false;

        this.managedObjects.push({ originalMesh: mesh, colliderBvh, colliderMesh });

        mesh.parent?.add(colliderMesh);
        mesh.parent?.updateMatrixWorld(true);
    }

    addChildObject(mesh: Mesh, includeStencilCap = true): void {
        const geometry = mesh.geometry;

        if (includeStencilCap) {
            const frontStencilMat = new MeshBasicMaterial({
                colorWrite: false,
                depthWrite: false,
                side: FrontSide,
                stencilWrite: true,
                stencilFail: IncrementWrapStencilOp,
                stencilZFail: IncrementWrapStencilOp,
                stencilZPass: IncrementWrapStencilOp,
                clippingPlanes: this.clippingPlanes
            });
            const frontStencil = new Mesh(geometry, frontStencilMat);
            frontStencil.name = mesh.name + '_StencilFront';

            const backStencilMat = new MeshBasicMaterial({
                colorWrite: false,
                depthWrite: false,
                side: BackSide,
                stencilWrite: true,
                stencilFail: DecrementWrapStencilOp,
                stencilZFail: DecrementWrapStencilOp,
                stencilZPass: DecrementWrapStencilOp,
                clippingPlanes: this.clippingPlanes
            });
            const backStencil = new Mesh(geometry, backStencilMat);
            backStencil.name = mesh.name + '_StencilBack';

            mesh.add(frontStencil, backStencil);
        }

        const colliderBvh = new MeshBVH(geometry, { maxLeafSize: 3 });

        const colliderMesh = new Mesh(geometry, new MeshBasicMaterial({ depthWrite: false }));
        colliderMesh.name = mesh.name + '_Collider';
        colliderMesh.visible = false;

        this.managedObjects.push({ originalMesh: mesh, colliderBvh, colliderMesh });

        mesh.add(colliderMesh);
    }


    async computeBVHAsync(workerService: GeometryWorkerService): Promise<void> {
        const managed = this.managedObjects[0];
        if (!managed?.colliderMesh || !workerService?.isSupported) return;

        const geometry = managed.colliderMesh.geometry;
        const positionAttr = geometry.getAttribute('position');
        const indexAttr = geometry.getIndex();

        if (!positionAttr || !indexAttr) return;

        try {
            const positions = positionAttr.array as Float32Array;
            const indices = indexAttr.array as Uint32Array;

            const result = await workerService.computeBVH(positions, indices, {
                maxLeafSize: 3
            });

            const bvh = MeshBVH.deserialize(result.serializedBVH, geometry);
            managed.colliderBvh = bvh;
            geometry.boundsTree = bvh;
        } catch (_e) {
            // Keep sync BVH if worker computation fails
        }
    }

    /**
     * Recompute BVH asynchronously for all managed objects.
     */
    async computeAllBVHAsync(workerService: GeometryWorkerService): Promise<void> {
        if (!workerService?.isSupported) return;

        const promises = this.managedObjects.map(async (managed) => {
            if (!managed.colliderMesh) return;

            const geometry = managed.colliderMesh.geometry;
            const positionAttr = geometry.getAttribute('position');
            const indexAttr = geometry.getIndex();
            if (!positionAttr || !indexAttr) return;

            try {
                const positions = positionAttr.array as Float32Array;
                const indices = indexAttr.array as Uint32Array;

                const result = await workerService.computeBVH(positions, indices, {
                    maxLeafSize: 3
                });

                const bvh = MeshBVH.deserialize(result.serializedBVH, geometry);
                managed.colliderBvh = bvh;
            } catch (_e) {
                // Keep sync BVH
            }
        });

        await Promise.all(promises);
    }

    // Slider semantics: 0 = no clip (plane past +axis edge). Positive slider
    // clips inward from the +axis side; negative flips the normal and clips
    // inward from the -axis side. Slider range is ±(full span) so |slider| =
    // halfExtent puts the plane at the model centre, and |slider| = 2·halfExtent
    // pushes it to the opposite edge (fully clipped). centerX/Y/Z and
    // halfX/Y/Z hold the world-space centre and half-extent of the buffered box.
    public planePositions = {
        x: 0,
        y: 0,
        z: 0,
        xMin: 0,
        xMax: 0,
        yMin: 0,
        yMax: 0,
        zMin: 0,
        zMax: 0,
        centerX: 0,
        centerY: 0,
        centerZ: 0,
        halfX: 0,
        halfY: 0,
        halfZ: 0
    };

    setPosition(value: number, type: 'x' | 'y' | 'z'): void {
        if (type === 'x') { this.planePositions.x = value; }
        else if (type === 'y') { this.planePositions.y = value; }
        else if (type === 'z') { this.planePositions.z = value; }
    }

    setMinMax(xMin: number, xMax: number, yMin: number, yMax: number, zMin: number, zMax: number): void {
        const fXMin = Math.floor(xMin);
        const rXMax = Math.round(xMax);
        const fYMin = Math.floor(yMin);
        const rYMax = Math.round(yMax);
        const fZMin = Math.floor(zMin);
        const rZMax = Math.round(zMax);

        this.planePositions.centerX = Math.round((fXMin + rXMax) / 2);
        this.planePositions.centerY = Math.round((fYMin + rYMax) / 2);
        this.planePositions.centerZ = Math.round((fZMin + rZMax) / 2);

        this.planePositions.halfX = Math.round((rXMax - fXMin) / 2);
        this.planePositions.halfY = Math.round((rYMax - fYMin) / 2);
        this.planePositions.halfZ = Math.round((rZMax - fZMin) / 2);

        // Slider can traverse the full span (2·half) so the plane can cross
        // from one edge to the other and fully clip the model from either side.
        this.planePositions.xMin = -2 * this.planePositions.halfX;
        this.planePositions.xMax = 2 * this.planePositions.halfX;
        this.planePositions.yMin = -2 * this.planePositions.halfY;
        this.planePositions.yMax = 2 * this.planePositions.halfY;
        this.planePositions.zMin = -2 * this.planePositions.halfZ;
        this.planePositions.zMax = 2 * this.planePositions.halfZ;

        this.planePositions.x = 0;
        this.planePositions.y = 0;
        this.planePositions.z = 0;
    }

    private tempVector = new Vector3();
    private tempVector1 = new Vector3();
    private tempVector2 = new Vector3();
    private tempVector3 = new Vector3();
    private tempLine = new Line3();
    private inverseMatrix = new Matrix4();
    private localPlane = new Plane();
    private clock = new Clock();
    private time = 0;

    /**
     * Checks if a mesh is effectively visible by walking up the parent chain.
     * Returns false if the mesh or any ancestor is hidden.
     */
    private isMeshEffectivelyVisible(mesh: Mesh): boolean {
        let current: Mesh | Scene | null = mesh;
        while (current && !(current instanceof Scene)) {
            if (!current.visible) return false;
            current = current.parent as Mesh | Scene | null;
        }
        return true;
    }

    performClipping(): void {
        const delta = Math.min(this.clock.getDelta(), 0.03);
        this.time += delta;

        // Compute combined bounding box from visible managed objects only
        const combinedBox = new Box3();
        for (const managed of this.managedObjects) {
            if (!this.isMeshEffectivelyVisible(managed.originalMesh)) continue;
            const objBox = new Box3().setFromObject(managed.originalMesh);
            combinedBox.union(objBox);
        }
        // Fallback to primary mesh if no managed objects (shouldn't happen)
        if (this.managedObjects.length === 0 && this.originalMesh) {
            combinedBox.setFromObject(this.originalMesh);
        }

        const size = new Vector3();
        const center = new Vector3();
        combinedBox.getSize(size);
        combinedBox.getCenter(center);

        // Slider 0 = no clip (plane sits past the +axis edge). Positive slider
        // pulls the plane inward from the +axis side; negative flips the normal
        // and pulls it inward from the -axis side.
        const halfX = this.planePositions.halfX;
        const halfY = this.planePositions.halfY;
        const halfZ = this.planePositions.halfZ;
        const sX = this.planePositions.x;
        const sY = this.planePositions.y;
        const sZ = this.planePositions.z;
        const worldX = sX >= 0
            ? this.planePositions.centerX + halfX - sX
            : this.planePositions.centerX - halfX - sX;
        const worldY = sY >= 0
            ? this.planePositions.centerY + halfY - sY
            : this.planePositions.centerY - halfY - sY;
        const worldZ = sZ >= 0
            ? this.planePositions.centerZ + halfZ - sZ
            : this.planePositions.centerZ - halfZ - sZ;

        if (!combinedBox.isEmpty()) {
            const maxDim = Math.max(size.x, size.y, size.z);

            this.planeMeshX.rotation.y = Math.PI / 2;
            this.planeMeshX.scale.setScalar(maxDim);
            this.planeMeshX.position.set(worldX, center.y, center.z);

            this.planeMeshY.rotation.x = Math.PI / 2;
            this.planeMeshY.scale.setScalar(maxDim);
            this.planeMeshY.position.set(center.x, worldY, center.z);

            this.planeMeshZ.scale.setScalar(maxDim);
            this.planeMeshZ.position.set(center.x, center.y, worldZ);

            this.planeMeshX.updateMatrixWorld();
            this.planeMeshY.updateMatrixWorld();
            this.planeMeshZ.updateMatrixWorld();
        }

        // Three.js Plane keeps points where (normal · p + constant) ≥ 0.
        // +slider: normal=-1 → keep p ≤ worldPos. -slider: normal=+1 → keep p ≥ worldPos.
        const clippingPlaneX = this.clippingPlanes[0];
        clippingPlaneX.normal.set(sX >= 0 ? -1 : 1, 0, 0);
        clippingPlaneX.constant = sX >= 0 ? worldX : -worldX;

        const clippingPlaneY = this.clippingPlanes[1];
        clippingPlaneY.normal.set(0, sY >= 0 ? -1 : 1, 0);
        clippingPlaneY.constant = sY >= 0 ? worldY : -worldY;

        const clippingPlaneZ = this.clippingPlanes[2];
        clippingPlaneZ.normal.set(0, 0, sZ >= 0 ? -1 : 1);
        clippingPlaneZ.constant = sZ >= 0 ? worldZ : -worldZ;

        if (this.generateEdges) {
            const hasAnyBvh = this.managedObjects.some(o => o.colliderBvh);

            if (hasAnyBvh) {
                this.initialClip = true;

                const findOutline = (posAttr: BufferAttribute | InterleavedBufferAttribute, clippingPlane: Plane, outlineLines: LineSegments) => {
                    let index = 0;

                    // Iterate all managed objects to combine edge outlines
                    for (const managed of this.managedObjects) {
                        if (!managed.colliderBvh || !managed.colliderMesh) continue;

                        // Skip edge detection for meshes that are not visible
                        // (or whose parent group is hidden)
                        if (!this.isMeshEffectivelyVisible(managed.originalMesh)) continue;

                        // Get the clipping plane in the local space of this BVH
                        this.inverseMatrix.copy(managed.colliderMesh.matrixWorld).invert();
                        this.localPlane.copy(clippingPlane).applyMatrix4(this.inverseMatrix);

                        managed.colliderBvh.shapecast({
                            intersectsBounds: box => {
                                return this.localPlane.intersectsBox(box);
                            },

                            intersectsTriangle: tri => {
                                let count = 0;
                                const worldMatrix = managed.colliderMesh!.matrixWorld;

                                this.tempLine.start.copy(tri.a);
                                this.tempLine.end.copy(tri.b);
                                if (this.localPlane.intersectLine(this.tempLine, this.tempVector)) {
                                    this.tempVector.applyMatrix4(worldMatrix);
                                    posAttr.setXYZ(index, this.tempVector.x, this.tempVector.y, this.tempVector.z);
                                    index++;
                                    count++;
                                }

                                this.tempLine.start.copy(tri.b);
                                this.tempLine.end.copy(tri.c);
                                if (this.localPlane.intersectLine(this.tempLine, this.tempVector)) {
                                    this.tempVector.applyMatrix4(worldMatrix);
                                    posAttr.setXYZ(index, this.tempVector.x, this.tempVector.y, this.tempVector.z);
                                    count++;
                                    index++;
                                }

                                this.tempLine.start.copy(tri.c);
                                this.tempLine.end.copy(tri.a);
                                if (this.localPlane.intersectLine(this.tempLine, this.tempVector)) {
                                    this.tempVector.applyMatrix4(worldMatrix);
                                    posAttr.setXYZ(index, this.tempVector.x, this.tempVector.y, this.tempVector.z);
                                    count++;
                                    index++;
                                }

                                // Deduplicate when plane passes through a vertex
                                if (count === 3) {
                                    this.tempVector1.fromBufferAttribute(posAttr, index - 3);
                                    this.tempVector2.fromBufferAttribute(posAttr, index - 2);
                                    this.tempVector3.fromBufferAttribute(posAttr, index - 1);

                                    if (this.tempVector3.equals(this.tempVector1) || this.tempVector3.equals(this.tempVector2)) {
                                        count--;
                                        index--;
                                    } else if (this.tempVector1.equals(this.tempVector2)) {
                                        posAttr.setXYZ(index - 2, this.tempVector3.x, this.tempVector3.y, this.tempVector3.z);
                                        count--;
                                        index--;
                                    }
                                }

                                if (count !== 2) {
                                    index -= count;
                                }
                            },
                        });
                    }

                    outlineLines.geometry.setDrawRange(0, index);
                    outlineLines.position.copy(clippingPlane.normal).multiplyScalar(-0.0001);
                    posAttr.needsUpdate = true;
                };

                findOutline(this.outlineLinesX.geometry.attributes['position'], clippingPlaneX, this.outlineLinesX);
                findOutline(this.outlineLinesY.geometry.attributes['position'], clippingPlaneY, this.outlineLinesY);
                findOutline(this.outlineLinesZ.geometry.attributes['position'], clippingPlaneZ, this.outlineLinesZ);
            }
        }
    }
}


// --- Uniforms for the Shader ---
// These will be passed to your GLSL shader
export const uniforms = {
    u_clippingPlane: { value: new Vector4() },
    u_lineColor: { value: new Color(0x000000) }, // Color of the diagonal lines
    u_baseColor: { value: new Color('#B4ECE6') }, // Color of the cut surface itself
    u_lineWidth: { value: 0.3 }, // Relative width of the lines
    u_lineSpacing: { value: 2 }, // Spacing of the lines
    u_lineAngle: { value: Math.PI / 4.0 } // Angle of the lines (45 degrees)
};

// --- Custom Shader Material ---
export const SectionMaterial = new ShaderMaterial({
    uniforms: uniforms,
    vertexShader: `
        varying vec3 vWorldPosition;
        varying vec3 vNormal;

        void main() {
            vNormal = normalMatrix * normal; // Transform normal to view space
            vec4 worldPosition = modelMatrix * vec4(position, 1.0);
            vWorldPosition = worldPosition.xyz;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    `,
    fragmentShader: `
        uniform vec4 u_clippingPlane;
        uniform vec3 u_lineColor;
        uniform vec3 u_baseColor;
        uniform float u_lineWidth;
        uniform float u_lineSpacing;
        uniform float u_lineAngle;

        varying vec3 vWorldPosition;
        varying vec3 vNormal;

        vec2 rotate(vec2 v, float a) {
            float s = sin(a);
            float c = cos(a);
            mat2 m = mat2(c, -s, s, c);
            return m * v;
        }

        void main() {
            vec3 planeNormal = normalize(u_clippingPlane.xyz);
            float planeConstant = u_clippingPlane.w;

            // Project world position onto a 2D plane defined by the clipping plane's orientation
            // Create two basis vectors orthogonal to the planeNormal
            vec3 tangent = normalize(cross(planeNormal, vec3(0.0, 1.0, 0.0)));
            if (length(tangent) < 0.1) { // Handle case where planeNormal is along Y
                tangent = normalize(cross(planeNormal, vec3(1.0, 0.0, 0.0)));
            }
            vec3 bitangent = normalize(cross(planeNormal, tangent));

            // Project vWorldPosition onto these basis vectors
            vec2 projectedPos = vec2(dot(vWorldPosition, tangent), dot(vWorldPosition, bitangent));

            // Rotate the coordinate system for diagonal lines
            vec2 rotatedPos = rotate(projectedPos, u_lineAngle);

            // Calculate a repeating pattern
            float pattern = mod(rotatedPos.x + rotatedPos.y, u_lineSpacing);

            vec3 finalColor = u_baseColor;
            if (pattern < u_lineWidth) {
                finalColor = u_lineColor;
            }

            gl_FragColor = vec4(finalColor, 1.0);
        }
    `,
    clipping: true, // This is important to make the ShaderMaterial respect the renderer's clipping planes
});
